import { useState } from "react";
import {
  Box,
  Calendar,
  Tag as TagIcon,
  File,
  Link as LinkIcon,
  Plus,
  X,
  Folder,
  Briefcase,
} from "lucide-react";
import type { ModelWithFilesAndLinks } from "@maker-toolchest/shared";
import { Button } from "@/components/Button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/Tabs";
import { EmptyState } from "@/components/EmptyState";
import {
  useAddFile,
  useAddLink,
  useDeleteFile,
  useDeleteLink,
  useRetryLinkMetadata,
} from "../hooks";
import { useRemoveModelsFromCollection } from "@/features/collections/hooks";
import { AddToCollectionSlideOver } from "@/features/collections/components";
import { MultiFileUpload } from "@/components/MultiFileUpload";
import { Input } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import { useToast } from "@/components/ToastProvider";
import { SlideOver } from "@/components/SlideOver";
import { LinkCard } from "@/components/LinkCard";
import { FilesTable } from "./FilesTable";
import { LinksTable } from "./LinksTable";
import { JobTable } from "@/features/jobs/components/JobTable";
import { useJobs } from "@/features/jobs/hooks";
import { useNavigate } from "react-router-dom";
import { formatDate, formatFileSize } from "@/utils/formatting";
import { ModelFilesTab, ModelJobsTab } from "./tabs";

interface ModelDetailViewProps {
  model: ModelWithFilesAndLinks;
  onNavigate?: (path: string) => void;
}

export function ModelDetailView({ model, onNavigate }: ModelDetailViewProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const [showAddFile, setShowAddFile] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [showAddToCollection, setShowAddToCollection] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set(),
  );
  const [jobsPage] = useState(1);

  const navigate = useNavigate();

  // Fetch jobs associated with this model (for Jobs tab)
  const { data: jobsData } = useJobs({
    modelId: model.id,
    page: jobsPage,
    limit: 25,
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  // Fetch recent unfinished jobs for overview summary (fetch 15 to ensure we get 3 after filtering)
  const { data: recentJobsData, isLoading: recentJobsLoading } = useJobs({
    modelId: model.id,
    page: 1,
    limit: 15,
    sortBy: "updatedAt",
    sortOrder: "desc",
  });

  // Filter to unfinished jobs (exclude COMPLETED and CANCELLED) and take first 3
  const unfinishedJobs =
    recentJobsData?.data
      ?.filter(
        (job) => job.status !== "COMPLETED" && job.status !== "CANCELLED",
      )
      .slice(0, 3) || [];

  // File form
  interface PendingFile {
    id: string;
    file: File;
  }
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  // Link form
  const [linkUrl, setLinkUrl] = useState("");
  const [linkNotes, setLinkNotes] = useState("");
  const [linkError, setLinkError] = useState("");

  const toast = useToast();
  const addFileMutation = useAddFile();
  const addLinkMutation = useAddLink();
  const deleteFileMutation = useDeleteFile();
  const deleteLinkMutation = useDeleteLink();
  const retryLinkMetadataMutation = useRetryLinkMetadata();
  const removeFromCollectionMutation = useRemoveModelsFromCollection();

  const handleMultipleFilesSelect = (files: File[]) => {
    if (files.length === 0) return;

    const newFiles = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));

    setPendingFiles((prev) => [...prev, ...newFiles]);

    const message =
      files.length === 1
        ? `${files[0].name} added to queue`
        : `${files.length} files added to queue`;
    toast.success(message);
  };

  const handleRemovePendingFile = (id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUploadFiles = async () => {
    if (pendingFiles.length === 0) return;

    try {
      // Upload all files
      for (const pendingFile of pendingFiles) {
        await addFileMutation.mutateAsync({
          modelId: model.id,
          file: pendingFile.file,
          input: {},
        });
      }

      const message =
        pendingFiles.length === 1
          ? "File uploaded successfully"
          : `${pendingFiles.length} files uploaded successfully`;
      toast.success(message);
      setPendingFiles([]);
      setShowAddFile(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to upload files");
    }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim()) return;

    try {
      new URL(linkUrl);
    } catch {
      setLinkError("Must be a valid URL");
      return;
    }

    try {
      await addLinkMutation.mutateAsync({
        modelId: model.id,
        input: {
          url: linkUrl.trim(),
          notes: linkNotes.trim() || undefined,
        },
      });

      toast.success("Link added successfully");
      setLinkUrl("");
      setLinkNotes("");
      setLinkError("");
      setShowAddLink(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to add link");
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!confirm("Are you sure you want to delete this file?")) return;

    try {
      await deleteFileMutation.mutateAsync({ modelId: model.id, fileId });
      toast.success("File deleted");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete file");
    }
  };

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    try {
      const { modelsApi } = await import("../api");
      const { url } = await modelsApi.getFileDownloadUrl(model.id, fileId);

      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      toast.error(error.message || "Failed to download file");
    }
  };

  const handleRetryMetadata = async (linkId: string) => {
    try {
      await retryLinkMetadataMutation.mutateAsync({
        modelId: model.id,
        linkId,
      });
      toast.success("Retrying metadata extraction");
    } catch (error: any) {
      toast.error(error.message || "Failed to retry metadata extraction");
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    if (!confirm("Are you sure you want to delete this link?")) return;

    try {
      await deleteLinkMutation.mutateAsync({ modelId: model.id, linkId });
      toast.success("Link deleted");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete link");
    }
  };

  const toggleDescriptionExpanded = (linkId: string) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(linkId)) {
        next.delete(linkId);
      } else {
        next.add(linkId);
      }
      return next;
    });
  };

  const handleRemoveFromCollection = async (collectionId: string) => {
    try {
      await removeFromCollectionMutation.mutateAsync({
        collectionId,
        input: { modelIds: [model.id] },
      });
      toast.success("Removed from collection");
    } catch (error: any) {
      toast.error(error.message || "Failed to remove from collection");
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <div className="border-b border-border px-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="files">
              Files ({model.files.length})
            </TabsTrigger>
            <TabsTrigger value="links">
              Links ({model.links.length})
            </TabsTrigger>
            <TabsTrigger value="jobs">
              Jobs{" "}
              {jobsData?.meta?.pagination &&
                jobsData.meta.pagination.total > 0 &&
                `(${jobsData.meta.pagination.total})`}
            </TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Overview Tab */}
          <TabsContent value="overview" className="p-6">
            {/* 2-column grid layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* TOP LEFT - Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="mb-3 text-lg font-medium text-foreground">
                    Details
                  </h3>
                  <div className="grid gap-4">
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Date Added
                      </div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {formatDate(model.addedAt)}
                      </div>
                    </div>
                    {model.license && (
                      <div className="rounded-lg border border-border bg-background-subtle p-4">
                        <div className="text-xs text-foreground-muted">
                          License
                        </div>
                        <div className="mt-1 text-sm font-medium text-foreground">
                          {model.license}
                        </div>
                      </div>
                    )}
                    {model.printCount > 0 && (
                      <div className="rounded-lg border border-border bg-background-subtle p-4">
                        <div className="text-xs text-foreground-muted">
                          Times Printed
                        </div>
                        <div className="mt-1 text-sm font-medium text-foreground">
                          {model.printCount}
                        </div>
                      </div>
                    )}
                    {model.lastPrinted && (
                      <div className="rounded-lg border border-border bg-background-subtle p-4">
                        <div className="text-xs text-foreground-muted">
                          Last Printed
                        </div>
                        <div className="mt-1 text-sm font-medium text-foreground">
                          {formatDate(model.lastPrinted)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {model.tags && model.tags.length > 0 && (
                  <div>
                    <h3 className="mb-3 text-lg font-medium text-foreground flex items-center gap-2">
                      <TagIcon className="h-5 w-5" />
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {model.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 text-sm bg-background-subtle text-foreground-muted rounded-full border border-border"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {model.notes && (
                  <div>
                    <h3 className="mb-3 text-lg font-medium text-foreground">
                      Notes
                    </h3>
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <p className="text-sm text-foreground whitespace-pre-wrap">
                        {model.notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* TOP RIGHT - Collections */}
              <div>
                <div>
                  <h3 className="mb-3 text-lg font-medium text-foreground flex items-center justify-between">
                    <span>Collections</span>
                    <Button
                      size="sm"
                      onClick={() => setShowAddToCollection(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add to Collection
                    </Button>
                  </h3>

                  {model.collections && model.collections.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {model.collections.map((mc) => (
                        <div
                          key={mc.id}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors hover:bg-background-subtle"
                          style={{
                            borderColor: mc.collection.color || "#e5e7eb",
                            backgroundColor: mc.collection.color
                              ? `${mc.collection.color}10`
                              : "#f3f4f6",
                          }}
                        >
                          <Folder
                            className="h-4 w-4 flex-shrink-0"
                            style={{ color: mc.collection.color || "#6b7280" }}
                          />
                          <span
                            className="font-medium text-sm"
                            style={{ color: mc.collection.color || "#6b7280" }}
                          >
                            {mc.collection.name}
                          </span>
                          <button
                            onClick={() =>
                              handleRemoveFromCollection(mc.collection.id)
                            }
                            disabled={removeFromCollectionMutation.isPending}
                            className="ml-1 p-0.5 rounded hover:bg-error/20 transition-colors disabled:opacity-50"
                            title="Remove from collection"
                          >
                            <X className="h-3.5 w-3.5 text-error" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
                      <Folder className="h-10 w-10 text-foreground-muted mx-auto mb-2" />
                      <p className="text-sm text-foreground-muted">
                        Not in any collections
                      </p>
                      <p className="text-xs text-foreground-muted mt-1">
                        Add this model to collections to organize your library
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* BOTTOM LEFT - Files Summary */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground flex items-center justify-between">
                  <span>Files ({model.files.length})</span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => setShowAddFile(true)}
                    >
                      Add Files
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setActiveTab("files")}
                    >
                      View All
                    </Button>
                  </div>
                </h3>

                {model.files.length === 0 ? (
                  <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
                    <File className="h-10 w-10 text-foreground-muted mx-auto mb-2" />
                    <p className="text-sm text-foreground-muted">
                      No files yet
                    </p>
                    <p className="text-xs text-foreground-muted mt-1">
                      Add files in the Files tab
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <FilesTable
                      modelId={model.id}
                      files={model.files}
                      onDownload={handleDownloadFile}
                      onFileClick={(fileId) =>
                        onNavigate?.(
                          `/models/${model.id}/files/${fileId}/details`,
                        )
                      }
                      maxRows={5}
                    />
                    {model.files.length > 5 && (
                      <div className="px-4 py-2 bg-background-subtle border-t border-border text-center">
                        <button
                          onClick={() => setActiveTab("files")}
                          className="text-xs text-primary hover:underline"
                        >
                          +{model.files.length - 5} more files
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* BOTTOM RIGHT - Links Summary */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground flex items-center justify-between">
                  <span>Links ({model.links.length})</span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => setShowAddLink(true)}
                    >
                      Add Link
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setActiveTab("links")}
                    >
                      View All
                    </Button>
                  </div>
                </h3>

                {model.links.length === 0 ? (
                  <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
                    <LinkIcon className="h-10 w-10 text-foreground-muted mx-auto mb-2" />
                    <p className="text-sm text-foreground-muted">
                      No links yet
                    </p>
                    <p className="text-xs text-foreground-muted mt-1">
                      Add links in the Links tab
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <LinksTable links={model.links} maxRows={5} />
                    {model.links.length > 5 && (
                      <div className="px-4 py-2 bg-background-subtle border-t border-border text-center">
                        <button
                          onClick={() => setActiveTab("links")}
                          className="text-xs text-primary hover:underline"
                        >
                          +{model.links.length - 5} more links
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Active Jobs Summary */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Active Jobs
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setActiveTab("jobs")}
                >
                  View All Jobs
                </Button>
              </div>

              {recentJobsLoading ? (
                <div className="flex items-center justify-center py-12 rounded-lg border border-border bg-background-subtle">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : unfinishedJobs.length > 0 ? (
                <JobTable
                  jobs={unfinishedJobs}
                  onAddTasks={(job) =>
                    navigate(`/projects/jobs/${job.id}?action=addTasks`)
                  }
                  onUpdateStatus={(job) =>
                    navigate(`/projects/jobs/${job.id}?action=updateStatus`)
                  }
                  onViewTasks={(job) => navigate(`/projects/jobs/${job.id}`)}
                  onCancelJob={(job) =>
                    navigate(`/projects/jobs/${job.id}?action=cancel`)
                  }
                />
              ) : (
                <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
                  <Briefcase className="h-10 w-10 text-foreground-muted mx-auto mb-2" />
                  <p className="text-sm text-foreground-muted">
                    No active jobs for this model
                  </p>
                  <p className="text-xs text-foreground-muted mt-1">
                    Create a job to start tracking work with this model
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" className="p-6">
            <ModelFilesTab
              model={model}
              onNavigate={onNavigate}
              onAddFile={() => setShowAddFile(true)}
              onDownloadFile={handleDownloadFile}
              onDeleteFile={handleDeleteFile}
              isDeleting={deleteFileMutation.isPending}
            />
          </TabsContent>

          {/* Links Tab */}
          <TabsContent value="links" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                  External Links
                </h3>
                <Button
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowAddLink(true)}
                >
                  Add Link
                </Button>
              </div>

              {/* Links List */}
              {model.links.length === 0 ? (
                <EmptyState
                  icon={<LinkIcon className="h-10 w-10" />}
                  title="No links yet"
                  description="Add links to external model sources"
                />
              ) : (
                <div className="space-y-3">
                  {model.links.map((link) => (
                    <LinkCard
                      key={link.id}
                      link={link}
                      onRetryMetadata={handleRetryMetadata}
                      onDelete={handleDeleteLink}
                      expandedDescriptions={expandedDescriptions}
                      onToggleDescription={toggleDescriptionExpanded}
                      isRetrying={retryLinkMetadataMutation.isPending}
                      isDeleting={deleteLinkMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="p-6">
            <ModelJobsTab model={model} />
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="p-6">
            <EmptyState
              icon={<Box className="h-10 w-10" />}
              title="No associated projects"
              description="This model is not associated with any projects yet."
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Add to Collection SlideOver */}
      <AddToCollectionSlideOver
        open={showAddToCollection}
        onClose={() => setShowAddToCollection(false)}
        modelIds={[model.id]}
        currentCollectionIds={
          model.collections?.map((mc) => mc.collection.id) || []
        }
      />

      {/* Add Files SlideOver */}
      <SlideOver
        open={showAddFile}
        onClose={() => {
          setShowAddFile(false);
          setPendingFiles([]);
        }}
        title="Add Files"
        description="Upload files to this model"
      >
        <div className="space-y-4">
          <MultiFileUpload
            onFilesSelect={handleMultipleFilesSelect}
            disabled={addFileMutation.isPending}
          />

          {/* Pending Files List */}
          {pendingFiles.length > 0 && (
            <div className="space-y-2">
              {pendingFiles.map((pendingFile) => (
                <div
                  key={pendingFile.id}
                  className="flex items-start justify-between gap-3 p-3 bg-background rounded-lg border border-border"
                >
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <File className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-foreground truncate">
                        {pendingFile.file.name}
                      </div>
                      <div className="text-xs text-foreground-muted">
                        {formatFileSize(pendingFile.file.size)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePendingFile(pendingFile.id)}
                    className="text-foreground-muted hover:text-error"
                    disabled={addFileMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleUploadFiles}
              disabled={pendingFiles.length === 0 || addFileMutation.isPending}
              loading={addFileMutation.isPending}
              className="flex-1"
            >
              Upload {pendingFiles.length > 0 && `(${pendingFiles.length})`}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddFile(false);
                setPendingFiles([]);
              }}
              disabled={addFileMutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </SlideOver>

      {/* Add Link SlideOver */}
      <SlideOver
        open={showAddLink}
        onClose={() => {
          setShowAddLink(false);
          setLinkUrl("");
          setLinkNotes("");
          setLinkError("");
        }}
        title="Add Link"
        description="Add an external link to this model"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground-muted">
            Paste a link from MakerWorld, Printables, Thingiverse, or any other
            site. The provider will be detected automatically.
          </p>
          <Input
            label="URL"
            type="url"
            value={linkUrl}
            onChange={(e) => {
              setLinkUrl(e.target.value);
              setLinkError("");
            }}
            error={linkError}
            placeholder="https://..."
            disabled={addLinkMutation.isPending}
          />
          <Textarea
            label="Notes (optional)"
            value={linkNotes}
            onChange={(e) => setLinkNotes(e.target.value)}
            disabled={addLinkMutation.isPending}
            rows={2}
          />
          <div className="flex gap-2">
            <Button
              onClick={handleAddLink}
              disabled={!linkUrl.trim() || addLinkMutation.isPending}
              loading={addLinkMutation.isPending}
              className="flex-1"
            >
              Add Link
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddLink(false);
                setLinkUrl("");
                setLinkNotes("");
                setLinkError("");
              }}
              disabled={addLinkMutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </SlideOver>
    </div>
  );
}
